import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SectionIntro, FaqList, CheckList } from './B2B';

// Secciones de contenido del diseño B2B (nov-2026) que comparten las páginas de
// Soluciones (cargadores, transportistas, proveedores de software): bloques de
// datos → maquetación, para que las tres se vean iguales. tone: 'white' | 'gray'.

const TONE = {
  white: 'py-20 md:py-24',
  gray: 'py-20 md:py-24 bg-slate-50 border-y border-slate-200',
};

export const Section = ({ tone = 'white', children, className = '' }) => (
  <section className={`${TONE[tone]} ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

/** Enlace de texto con flecha (interno con `to`, externo con `href`). */
export const TextLink = ({ to, href, children, className = '' }) => {
  const cls = `inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:gap-3 transition-all ${className}`;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{children} <ArrowRight className="w-4 h-4" /></a>;
  return <Link to={to} className={cls}>{children} <ArrowRight className="w-4 h-4" /></Link>;
};

const GRID_COLS = { 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' };

/** Recorrido numerado con el lugar de cada paso. steps = [{ place, title, text }]. */
export const FlowSection = ({ eyebrow, title, text, steps, tone }) => (
  <Section tone={tone}>
    <SectionIntro eyebrow={eyebrow} title={title} text={text} />
    <ol className={`grid sm:grid-cols-2 ${GRID_COLS[steps.length] || 'lg:grid-cols-4'} gap-x-8 gap-y-10`}>
      {steps.map((step, i) => (
        <li key={step.title} className="border-t-2 border-slate-900 pt-5">
          <p className="flex items-baseline gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
            <span className="tabular-nums">{String(i + 1).padStart(2, '0')}</span>
            <span>{step.place}</span>
          </p>
          <h3 className="mt-3 font-semibold text-slate-900">{step.title}</h3>
          <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{step.text}</p>
        </li>
      ))}
    </ol>
  </Section>
);

/** Rejilla de puntos con filete. items = [{ title, text, to?, link? }]. */
export const ItemsSection = ({ eyebrow, title, text, items, cols = 3, tone, link }) => (
  <Section tone={tone}>
    <SectionIntro eyebrow={eyebrow} title={title} text={text} />
    <div className={`grid sm:grid-cols-2 ${GRID_COLS[cols]} gap-x-10 gap-y-10`}>
      {items.map((item) => (
        <div key={item.title} className="border-t border-slate-300 pt-6">
          <h3 className="font-semibold text-slate-900">{item.title}</h3>
          <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
          {item.to && <TextLink to={item.to} className="mt-4">{item.link}</TextLink>}
        </div>
      ))}
    </div>
    {link && <TextLink to={link.to} href={link.href} className="mt-12">{link.label}</TextLink>}
  </Section>
);

/** Lista de documentos enlazados, con su entradilla a la izquierda. docs = [{ title, text, to }]. */
export const DocsSection = ({ eyebrow, title, text, docs, tone }) => (
  <Section tone={tone}>
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <SectionIntro eyebrow={eyebrow} title={title} text={text} className="" />
      </div>
      <ul className="lg:col-span-7 border-b border-slate-300">
        {docs.map((d) => (
          <li key={d.title} className="border-t border-slate-300">
            <Link to={d.to} className="group flex items-start justify-between gap-4 py-5">
              <span>
                <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{d.title}</span>
                <span className="mt-1 block text-sm text-slate-600">{d.text}</span>
              </span>
              <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </Section>
);

/** Preguntas frecuentes con el título a la izquierda. */
export const FaqSection = ({ eyebrow, title, items, tone }) => (
  <Section tone={tone}>
    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-4">
        <SectionIntro eyebrow={eyebrow} title={title} className="" />
      </div>
      <div className="lg:col-span-8">
        <FaqList items={items} />
      </div>
    </div>
  </Section>
);

/** Checks del hero, en blanco sobre la foto. */
export const HeroChecks = ({ items }) => (
  <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
    {items.map((item) => (
      <li key={item} className="flex items-center gap-2 text-slate-200">
        <Check className="w-4 h-4 text-blue-300" strokeWidth={2.5} />
        {item}
      </li>
    ))}
  </ul>
);

/**
 * Tabla de filas con filete (tres columnas en escritorio, apiladas en móvil).
 * head = [col1, col2, col3]; rows = [{ title, href?, to?, sub?, text, aside }].
 * `aside` va en azul (p. ej. el esfuerzo de una integración o cómo lo aplica BlueCMR).
 */
export const RowsTable = ({ head, rows, asideTone = 'accent' }) => (
  <div className="border-b border-slate-300">
    <div className="hidden md:grid grid-cols-12 gap-8 pb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
      <span className="col-span-3">{head[0]}</span>
      <span className="col-span-5">{head[1]}</span>
      <span className="col-span-4">{head[2]}</span>
    </div>
    {rows.map((r) => {
      const title = r.href
        ? <a href={r.href} target="_blank" rel="noopener noreferrer" className="hover:text-primary-700 underline decoration-slate-300 underline-offset-4 hover:decoration-primary-700">{r.title}</a>
        : r.to ? <Link to={r.to} className="hover:text-primary-700 underline decoration-slate-300 underline-offset-4 hover:decoration-primary-700">{r.title}</Link> : r.title;
      return (
        <div key={r.title} className="grid md:grid-cols-12 gap-x-8 gap-y-2 border-t border-slate-300 py-6">
          <div className="md:col-span-3">
            <h3 className="font-semibold text-slate-900">{title}</h3>
            {r.sub && <p className="mt-1 text-sm text-slate-500">{r.sub}</p>}
          </div>
          <p className="md:col-span-5 text-[15px] text-slate-600 leading-relaxed">{r.text}</p>
          <p className={`md:col-span-4 text-[15px] leading-relaxed ${asideTone === 'accent' ? 'font-semibold text-primary-700 text-sm' : 'text-slate-800'}`}>{r.aside}</p>
        </div>
      );
    })}
  </div>
);

/**
 * Captura de móvil con marco fino y pie: capturas reales del producto con datos
 * ficticios (marketing/capturas-producto/visor-conductor.cjs), 600×1290.
 */
export const PhoneShot = ({ src, alt, caption }) => (
  <figure className="mx-auto w-60 md:w-full">
    <div className="rounded-[1.75rem] border border-slate-300 bg-white p-2 shadow-sm">
      <img src={src} alt={alt} width="600" height="1290" loading="lazy" className="block w-full h-auto rounded-[1.25rem] border border-slate-200" />
    </div>
    {caption && <figcaption className="mt-3 text-sm leading-6 text-slate-500 text-center">{caption}</figcaption>}
  </figure>
);

/**
 * Dos columnas con filete grueso, título, entradilla y lista con checks.
 * cols = [{ icon?, title, text, items }]; aside: p. ej. un <PhoneShot> a la izquierda.
 */
export const TwoColumns = ({ cols, aside }) => (
  <div className={`grid ${aside ? 'md:grid-cols-[minmax(0,15rem)_1fr_1fr] gap-x-14' : 'md:grid-cols-2 gap-x-16'} gap-y-14`}>
    {aside}
    {cols.map((col) => {
      const Icon = col.icon;
      return (
        <div key={col.title} className="border-t-2 border-slate-900 pt-6">
          {Icon && <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />}
          <h3 className="text-2xl font-semibold tracking-tight text-slate-900">{col.title}</h3>
          {col.text && <p className="mt-3 text-slate-600 leading-relaxed">{col.text}</p>}
          {col.items && <CheckList items={col.items} className="mt-6 space-y-2.5" />}
          {col.link && <TextLink to={col.link.to} href={col.link.href} className="mt-7">{col.link.label}</TextLink>}
        </div>
      );
    })}
  </div>
);

/** Franja final de páginas relacionadas. items = [{ title, text, to }]. */
export const RelatedSection = ({ title, items }) => (
  <section className="py-14 border-t border-slate-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">{title}</h2>
      <ul className="grid md:grid-cols-3 gap-x-8">
        {items.map((r) => (
          <li key={r.to} className="border-t border-slate-200">
            <Link to={r.to} className="group flex items-start justify-between gap-4 py-5">
              <span>
                <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{r.title}</span>
                <span className="mt-1 block text-sm text-slate-600">{r.text}</span>
              </span>
              <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </section>
);
